import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useFocusTrap } from '@/app/layers/useFocusTrap';
import { useLayer } from '@/app/layers/useLayer';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './Catalog.module.css';
import type { FilterGroupKey } from './filterModel';
import type { FilterGroup, FilterItem } from './useFilterGroups';

interface Props {
  groups: FilterGroup[];
  activeCount: number;
  /** ≤860: поповер становится нижним шитом (CAT-16). */
  isSheet: boolean;
  onReset: () => void;
  onOpenChange?: (open: boolean) => void;
}

type OpenKey = FilterGroupKey | 'all' | null;

const POPOVER_WIDTH = 220;

/** Ряд чипов фильтров с поповерами; на мобильном — кнопка «All» и шит (CAT-2, CAT-5, CAT-16). */
export function FilterChips({ groups, activeCount, isSheet, onReset, onOpenChange }: Props) {
  const t = useT();
  const rowRef = useRef<HTMLDivElement>(null);
  const [openKey, setOpenKey] = useState<OpenKey>(null);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    onOpenChange?.(openKey !== null);
  }, [openKey, onOpenChange]);

  const close = () => setOpenKey(null);

  const openGroup = (key: OpenKey, event?: MouseEvent<HTMLButtonElement>) => {
    if (openKey === key) return close();
    if (event && rowRef.current) {
      const chip = event.currentTarget.getBoundingClientRect();
      const row = rowRef.current.getBoundingClientRect();
      setLeft(Math.max(0, Math.min(chip.left - row.left, row.width - POPOVER_WIDTH)));
    } else {
      setLeft(0);
    }
    setOpenKey(key);
  };

  const openGroupData = openKey && openKey !== 'all' ? groups.find((g) => g.key === openKey) : null;

  return (
    <div ref={rowRef} className={styles.chipsRow}>
      <button
        type="button"
        className={cx(
          styles.allButton,
          (openKey === 'all' || activeCount > 0) && styles.allButtonActive,
        )}
        onClick={() => openGroup('all')}
        aria-haspopup="dialog"
        aria-expanded={openKey === 'all'}
      >
        <span className={styles.allIcon} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        {t('catalog.all')}
        {activeCount > 0 ? <span className={styles.countBadge}>{activeCount}</span> : null}
      </button>

      <div className={styles.chips}>
        {groups.map((group) => (
          <button
            key={group.key}
            type="button"
            className={cx(
              styles.chip,
              group.count > 0 && styles.chipActive,
              openKey === group.key && styles.chipOpen,
            )}
            onClick={(event) => openGroup(group.key, event)}
            aria-haspopup="dialog"
            aria-expanded={openKey === group.key}
          >
            {group.label}
            {group.count > 0 ? <span className={styles.countBadge}>{group.count}</span> : null}
            <span className={styles.chipCaret} aria-hidden="true">
              ▾
            </span>
          </button>
        ))}
        {activeCount > 0 ? (
          <button type="button" className={styles.resetAll} onClick={onReset}>
            {t('catalog.resetAll')}
          </button>
        ) : null}
      </div>

      {openKey ? (
        <FilterPopover
          title={openKey === 'all' ? 'Filters' : (openGroupData?.label ?? '')}
          groups={openKey === 'all' ? groups : openGroupData ? [openGroupData] : []}
          showHeaders={openKey === 'all'}
          left={left}
          isSheet={isSheet}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

interface PopoverProps {
  title: string;
  groups: FilterGroup[];
  showHeaders: boolean;
  left: number;
  isSheet: boolean;
  onClose: () => void;
}

function FilterPopover({ title, groups, showHeaders, left, isSheet, onClose }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const t = useT();
  // Шит — слой: «Назад» и Esc закрывают, скролл под ним заблокирован (NAV-10, NFR-7).
  useLayer(isSheet, onClose, { lockScroll: true });
  useFocusTrap(panelRef, isSheet);

  useEffect(() => {
    if (isSheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSheet, onClose]);

  return (
    <>
      <div className={cx(styles.backdrop, isSheet && styles.backdropSheet)} onClick={onClose} />
      <div
        ref={panelRef}
        className={styles.popover}
        style={{ left }}
        role={isSheet ? 'dialog' : 'group'}
        aria-modal={isSheet || undefined}
        aria-label={title}
        tabIndex={-1}
      >
        <div className={styles.popHead}>
          <span className={styles.popTitle}>{title}</span>
          <button type="button" className={styles.popDone} onClick={onClose}>
            {t('common.done')}
          </button>
        </div>
        {groups.map((group) => (
          <div key={group.key}>
            {showHeaders ? <p className={styles.popGroupHeader}>{group.label}</p> : null}
            {group.items.map((item) => (
              <FilterRow key={item.key} item={item} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

function FilterRow({ item }: { item: FilterItem }) {
  const t = useT();
  return (
    <div className={styles.item}>
      <button
        type="button"
        role="checkbox"
        aria-checked={item.on}
        className={cx(
          styles.itemToggle,
          item.child && styles.itemToggleChild,
          item.on && styles.itemOn,
        )}
        onClick={item.onToggle}
      >
        <span className={cx(styles.box, item.on && styles.boxOn)} aria-hidden="true">
          {item.on ? <span className={styles.check} /> : null}
        </span>
        <span className={styles.itemName}>{item.name}</span>
        {item.sub ? <span className={styles.itemSub}>{item.sub}</span> : null}
      </button>
      {item.hasKids ? (
        <button
          type="button"
          className={styles.expand}
          title={t('catalog.subcategories')}
          aria-label={t(item.expanded ? 'catalog.collapse' : 'catalog.expand', { name: item.name })}
          aria-expanded={item.expanded}
          onClick={item.onExpand}
        >
          {item.expanded ? '▾' : '▸'}
        </button>
      ) : (
        <span className={styles.expandSpacer} />
      )}
    </div>
  );
}
